import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';

import { Field } from '../Field/Field.js'
import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import Grid from '@mui/material/Grid';

import { returnField } from '../../utils/formHelpers';

import CardWrapper from '../common/CardWrapper';

import { withTranslation } from 'react-i18next';

import { createAttr } from '../../store/actions/attrActions';
import { useTheme } from '@mui/material';
import { fastDeepEqual } from '../../utils/object.js'


const inlineStyles = theme => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    color: '#000',
  },
  tabRoot: {},
  button: {
    margin: theme.spacing(1),
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  rightIcon: {
    marginLeft: theme.spacing(1),
  },
  iconSmall: {
    fontSize: 20,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
});

// Check that the attribute name is unique by comparing lowercased versions of attribute names
// Display an error message if not - this prevents form submission
const checkUnique = (value, props) => {
  const { t } = props;
  const lowercaseWithoutSpaces = str => str.replace(' ', '').toLowerCase();
  const isEqual = n =>
    lowercaseWithoutSpaces(value) ===
      lowercaseWithoutSpaces(R.path(['key'], n)) ||
    lowercaseWithoutSpaces(value) ===
      lowercaseWithoutSpaces(R.path(['label', 'en'], n)) ||
    lowercaseWithoutSpaces(value) ===
      lowercaseWithoutSpaces(R.path(['label', 'fi'], n));
  const matchingAttributes =
    value && R.filter(isEqual, R.path(['attr'], props));
  return value && R.keys(matchingAttributes).length > 0
    ? t('createAttributes.attributeExists')
    : undefined;
};

const CreateAttr = ({
  attr,
  createAttr,
  initialValues,
  t,
}) => {
  const [formValues, setFormValues] = useState(initialValues)
  const [submitting, setSubmitting] = useState(false)

  const pristine = fastDeepEqual(initialValues, formValues)
  const theme = useTheme();
  const classes = inlineStyles(theme);

  const handleSubmit = useCallback(async event => {
    event.preventDefault()

    const validationError =
      checkUnique(formValues.label.fi, { attr, t }) ||
      checkUnique(formValues.label.en, { attr, t })

    if (validationError) {
      alert(validationError)
      return false
    }

    setSubmitting(true)
    await createAttr(formValues)
    setSubmitting(false)
    setFormValues(structuredClone(emptyCreateAttrForm))
  }, [
    attr,
    createAttr,
    formValues,
    t
  ])

  return (
    <CardWrapper name={t('createAttributes.createNewAttribute')} headline>
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={4}
          direction="row"
          alignContent="flex-end"
          alignItems="flex-end"
        >
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('text')}
              fullWidth
              label={t('createAttributes.label.finnish')}
              name="label.fi"
              required
              setValue={setFormValues}
              type="text"
              validate={[checkUnique]}
              value={formValues.label.fi}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('text')}
              fullWidth
              label={t('createAttributes.label.english')}
              name="label.en"
              required
              setValue={setFormValues}
              type="text"
              validate={[checkUnique]}
              value={formValues.label.en}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('text')}
              fullWidth
              label={t('createAttributes.description.finnish')}
              name="desc.fi"
              setValue={setFormValues}
              type="text"
              value={formValues.desc.fi}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('text')}
              fullWidth
              label={t('createAttributes.description.english')}
              name="desc.en"
              setValue={setFormValues}
              type="text"
              value={formValues.desc.en}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('select')}
              fullWidth
              id="fieldCategory"
              label={t('createAttributes.attributeCategory')}
              name="fieldCategory"
              required
              selOpts={[
                'basicInfo',
                'spaceInfo',
                'economicInfo',
                'parking',
                'realEstateServices',
                'realEstateTechnical',
                'risks',
                'additionalInfo',
              ]}
              setValue={setFormValues}
              translateOptions
              type="text"
              value={formValues.fieldCategory}
            />
          </Grid>
          <Grid item sm={12} md={6}>
            <Field
              component={returnField('select')}
              fullWidth
              id="fieldType"
              label={t('createAttributes.attributeInputFieldType')}
              name="fieldType"
              required
              selOpts={['text', 'number', 'textarea', 'select']}
              setValue={setFormValues}
              translateOptions
              type="text"
              value={formValues.fieldType}
            />
          </Grid>
          <Grid item sm={12} md={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={classes.button}
              disabled={submitting || pristine}
            >
              {t('createAttributes.saveAttribute')}
              <Icon sx={classes.rightIcon}>cloud_upload</Icon>
            </Button>
          </Grid>
        </Grid>
      </form>
    </CardWrapper>
  );
};

const emptyCreateAttrForm = {
  fieldCategory: 'basicInfo',
  fieldType: 'text',
  label: { en: '', fi: '' },
  desc: { en: '', fi: '' }
};

const mapStateToProps = state => {
  const attr = R.path(['attr', 'attr'], state);
  return {
    initialValues: structuredClone(emptyCreateAttrForm),
    attr,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  createAttr: attr => dispatch(createAttr(attr, R.path(['t'], ownProps))),
});

export default withTranslation('translations')(
  connect(mapStateToProps, mapDispatchToProps)(CreateAttr)
);
